import classNames from 'classnames'
import * as dateFns from 'date-fns'
import * as React from 'react'

import { StatelessDatePicker } from '../../../../components/date-picker'
import { getExtendedDialogState } from '../../../../components/dialog'
import { Input, InputComponentProps } from '../../../../components/input'
import { usePopoverState } from '../../../../components/popover'
import { Tooltip, TooltipTrigger, useTooltipState } from '../../../../components/tooltip'
import { PORTFOLIO_DATE_FORMAT_DATA, PORTFOLIO_DATE_FORMAT_VIEW } from '../../constants/general'
import { parseInputAsDate } from '../../utils'
import { usePortfolioBulkEditRowContext } from '../PortfolioBulkEditRowContext'
import { EditColumnProps } from './types'

const DATE_MAX_LENGTH = 10

function preventDefault(ev: React.MouseEvent<HTMLInputElement>) {
  ev.preventDefault()
}

interface StateType {
  date?: string
}
interface ErrorsType {
  date?: boolean
}

interface DateEditColumnWithPickerProps<TState, TErrors> extends EditColumnProps<TState, TErrors> {
  onIsInteractingChange: (isInteracting: boolean) => void
  inputElement: React.ReactElement<InputComponentProps>
  value: { input: string | undefined; picker: Date }
}

export function DateEditColumnWithPicker<TState extends StateType, TErrors extends ErrorsType>(
  props: DateEditColumnWithPickerProps<TState, TErrors>
) {
  const pickerState = usePopoverState()
  const pickerExtendedState = getExtendedDialogState(pickerState)
  const tooltipState = useTooltipState({ placement: 'top', unstable_offset: [-9, 10] })
  const tooltipExtendedState = getExtendedDialogState(tooltipState)
  const WrapperComponent = props.errors?.date ? TooltipTrigger : React.Fragment
  const wrapperProps = props.errors?.date
    ? {
        state: tooltipState,
        tabIndex: -1,
        onMouseEnter: preventDefault,
        onMouseLeave: preventDefault,
      }
    : {}

  const isInteractingWithDateInput = !pickerExtendedState.isFullyClosed || !tooltipExtendedState.isFullyClosed
  React.useEffect(() => {
    props.onIsInteractingChange(isInteractingWithDateInput)
    // We don't need to pass entire props as we check only if onIsInteractingChange changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onIsInteractingChange, isInteractingWithDateInput])

  /**
   * Handle date changes
   */
  const onDateChange = React.useRef((dateValue: Date | string) => {
    // Convert input to correct format if valid
    const parsedDate = parseInputAsDate({ input: dateValue })

    if (typeof dateValue === 'string' && !dateFns.isValid(parsedDate)) {
      props.setState((entry) => ({ ...entry, date: dateValue }))
      return
    }

    const date = dateFns.format(parsedDate, PORTFOLIO_DATE_FORMAT_DATA)
    props.setErrors((errors) => ({ ...errors, date: false }))
    props.setState((entry) => ({ ...entry, date }))
    tooltipState.hide()
  })

  /**
   * Reset error state on blur when editing manually
   */
  const handleBlur = React.useRef(() => {
    const date = parseInputAsDate({ input: props.entry.date ?? '' })
    if (dateFns.isValid(date)) {
      props.setErrors((errors) => ({ ...errors, date: false }))
      tooltipState.hide()
    }
  })

  return (
    <WrapperComponent {...(wrapperProps as any)}>
      <StatelessDatePicker state={pickerState} value={props.value.picker} onChange={onDateChange.current}>
        {React.cloneElement<InputComponentProps>(props.inputElement, {
          onChange: (ev) => onDateChange.current(ev.currentTarget.value),
          onKeyDown: () => pickerState.hide(),
          onBlur: handleBlur.current,
        })}
      </StatelessDatePicker>
      {!tooltipExtendedState.isFullyClosed && (
        <Tooltip state={tooltipState} color="red" className="max-w-48">
          Invalid date, please enter date in MM-DD-YYYY format.
        </Tooltip>
      )}
    </WrapperComponent>
  )
}

export function DateEditColumn<TState extends StateType, TErrors extends ErrorsType>(
  props: EditColumnProps<TState, TErrors>
) {
  const { isActive, setIsInteracting } = usePortfolioBulkEditRowContext()

  /**
   * Get formatted value for picker and input
   */
  const value = React.useMemo(() => {
    try {
      const parsedDate = parseInputAsDate({
        input: props.entry.date ?? '',
        format: PORTFOLIO_DATE_FORMAT_DATA,
        checkFormat: false,
      })

      return {
        input: dateFns.format(parsedDate, PORTFOLIO_DATE_FORMAT_VIEW),
        picker: parsedDate,
      }
    } catch {
      return {
        input: props.entry.date,
        picker: new Date(),
      }
    }
  }, [props.entry.date])

  const inputElement = (
    <Input
      isMobileStyleEnabled={false}
      name="date"
      label={props.label}
      isHorizontalLabel={props.isHorizontalLabel}
      labelClassName={props.labelClassName}
      type="text"
      form={props.form}
      className={classNames(props.className, { 'is-error': props.errors?.date })}
      inputClass="text-left tabular-nums"
      value={value.input}
      onChange={() => {}}
      error={props.errors?.date}
      size={props.size}
      maxLength={DATE_MAX_LENGTH}
      data-testid="input-date"
    />
  )

  if (isActive) {
    return (
      <DateEditColumnWithPicker
        inputElement={inputElement}
        value={value}
        onIsInteractingChange={setIsInteracting}
        {...props}
      />
    )
  }

  return inputElement
}
